/* eslint-disable @typescript-eslint/no-explicit-any */
import {HttpClient} from './HttpClient';
import {PROJECT_CONFIGS} from './projectConfigs';
import type {AuthStrategy} from './types';
import type {ProjectKey} from './projectConfigs';
import type {ProjectConfig} from './types';

export {HttpClient} from './HttpClient';
export {HttpError} from './types';
export {PROJECT_CONFIGS} from './projectConfigs';
export type {ProjectKey} from './projectConfigs';
export type {ProjectConfig, AuthStrategy, RequestOptions, HttpMethod} from './types';

/**
 * Creates an HttpClient instance for the given project.
 *
 * @param project - A named project key (e.g. 'INTERNAL', 'SHARED_MAPS') or a custom ProjectConfig.
 *   Passing a custom ProjectConfig is useful in tests where you want to point at an emulator or mock.
 * @param authStrategy - Auth mechanism to use. Defaults to 'bearer' (Firebase ID token).
 *   Use 'appcheck' for endpoints that require Firebase App Check (e.g. external-user audit logging).
 *
 * @example Standard usage
 *   const client = createHttpClient('INTERNAL');
 *   const result = await client.post<MyType>('myCloudFunction', { key: 'value' });
 *
 * @example Cross-project call with typed response
 *   const client = createHttpClient('NORTHPOINT_PRODUCTION');
 *   const {salesforceId} = await client.post<{salesforceId: string}>('createSalesforceRecord', body);
 *
 * @example Force token refresh
 *   const client = createHttpClient('SHARED_MAPS');
 *   const users = await client.get<UserInfo[]>('retrieveFirebaseUsers', {forceTokenRefresh: true});
 *
 * @example In tests - inject a custom URL resolver
 *   const client = createHttpClient({getUrl: (functionName, isProd, path) => `http://localhost:5001/my-project/us-central1/${functionName}/${path}`});
 */
const createHttpClient = (project: ProjectKey | ProjectConfig, authStrategy?: AuthStrategy): HttpClient => {
  const config: ProjectConfig = typeof project === 'string' ? PROJECT_CONFIGS[project] : project;
  return new HttpClient(config, authStrategy);
};

export {createHttpClient};

// ---------------------------------------------------------------------------
// Deprecated compatibility shims
// These re-export the old functional API so existing call sites continue to
// work during the Phase 2 migration. Remove once all callers have been updated.
// ---------------------------------------------------------------------------

/** @deprecated Use `createHttpClient('INTERNAL').post(functionName, body)` instead. */
const getCloudFunctionUrl = (functionName: string): string => {
  return createHttpClient('INTERNAL').getUrl(functionName);
};

/** @deprecated Use `createHttpClient(projectKey).get/post(functionName, body)` instead. */
const cloudFunctionFetch = async <T = any>({
  url,
  idToken,
  method = 'POST',
  headers = {},
  body,
  contentType = 'application/json',
}: {
  url: string;
  idToken: string | undefined;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  headers?: Record<string, string>;
  body?: any;
  contentType?: string;
}): Promise<T> => {
  if (!url) throw new Error('URL is required for the request.');
  if (!idToken) throw new Error('ID token is required for the request.');

  const mergedHeaders: Record<string, string> = {
    'Authorization': `Bearer ${idToken}`,
    ...(contentType ? {'Content-Type': contentType} : {}),
    ...headers,
  };

  const fetchOptions: RequestInit = {method, headers: mergedHeaders};

  if (body !== undefined && body !== null) {
    fetchOptions.body =
      typeof body === 'string' || contentType !== 'application/json'
        ? body
        : JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(url, fetchOptions);
  } catch (error) {
    throw new Error(`Fetch error: ${(error as Error).message}`);
  }

  const {parseResponse} = await import('./parseResponse');
  return parseResponse<T>(response);
};

export {cloudFunctionFetch, getCloudFunctionUrl};
